import validateConfig from '/business/shared/functions/validateConfig';

import { contexts, locales } from '/shared/util/Statics';
import type { Config } from '/index';
import type { DecoratedQuestionTreeItem, QuestionTreeItem } from '/business/shared/functions/types';

import testLogo from '/business/cmpdefault/images/cmpdefault-logo.svg';
import otherFuelsEducation from '/business/cmpdefault/components/questions/education/other-fuels.json';
import electricityEducation from '/business/cmpdefault/components/questions/education/electricity.json';

// QUESTIONS
import { getQuestionsLookup } from '/business/cmpdefault/components/questions/questions';
import decorateQuestions from '/business/shared/functions/decorateQuestions';
import flattenQuestions from '/business/shared/functions/flattenQuestions';

import ExampleReductionDocumentEN from '/business/shared/images/locale/en/example-reduction-document.png';

import ExampleReductionDocumentXX from '/business/shared/images/locale/xx/example-reduction-document.png';

import ExampleReductionDocumentZH from '/business/shared/images/locale/zh/example-reduction-document.png'; // TODO: need chinese version

// INFOCARDS
import {
  barGraphLayoutIsVerticallyScalable,
  infoCards,
  introCards,
  reductionCards,
  reductionInstructions,
} from '/business/cmpdefault/reducePage';

import { landingPageIntroSteps } from '/business/cmpdefault/landingPage';

type ConfigQuestionMethods = Pick<Config, 'getDecoratedQuestions' | 'getFlattenedQuestions' | 'getQuestionsLookup'>;
type WhitelabelConfig = Omit<Config, keyof ConfigQuestionMethods> & Partial<ConfigQuestionMethods>;
type FlattenedQuestionCache = Partial<Record<'deep' | 'shallow', QuestionTreeItem[]>>;

const getConfiguredQuestionsLookup = (): QuestionTreeItem[] => {
  const questionsLookup = config.getQuestionsLookup;
  if (!questionsLookup) {
    throw new Error('Questions lookup is not configured');
  }
  return questionsLookup();
};

const getConfiguredDecoratedQuestions = (): DecoratedQuestionTreeItem[] => {
  const decoratedQuestions = config.getDecoratedQuestions;
  if (!decoratedQuestions) {
    throw new Error('Decorated questions are not configured');
  }
  return decoratedQuestions();
};

const config: WhitelabelConfig = {
  app: contexts.CMPDEFAULT,
  name: 'cmpdefault',
  transKey: '/cmpdefault',
  findOutMoreLink: 'https://www.castrol.com/360dealer',
  isAppDisabled: false,

  findOutMoreLinkLandingPage: 'https://www.bp.com/en_gb/carbon-connect/home/our-carbon-credit-portfolio.html',
  emissionTypes: {
    // needs to be commented out if there are less questions than in castrol version
    refrigerants: 'refrigerants',
    otherFuels: 'other',
    companyVehicles: 'company-vehicles',
    electricity: 'electricity',
    evCharging: 'ev-charging',
    // random: 'random',
    businessTravel: 'business-travel',
    commutes: 'employee-commutes',
    // smallSource: 'misc',
  },
  scopeKeys: {
    'scope-1': 'Scope 1',
    'scope-2': 'Scope 2',
    'scope-3': 'Scope 3',
  },
  emissionFields: {
    location: {
      hasOEM: false,
    },
    electricity: {
      hasSourceBreakdown: false,
    },
    companyVehiclesTotalFuelConsumption: {
      hasEvCharging: false,
    },
  },
  reducePage: {
    barGraphLayoutIsVerticallyScalable,
    hasUpdatedEmissionsTargetIntro: true,
    introCards,
    infoCards,
    reductionCardsV3: [],
    reductionCards,
    reductionInstructions,
    hasSubtitle: true,
    hasShortenedCarbonExplainer: true,
    isBasketSticky: false,
    baselineHasUpdatedDesign: false,
    reductionsConfig: {
      showEmptyScopes: false,
      isMinimumReduction: false,
      minimumReductionPercentage: null,
      hasAspirationalOffsetTarget: true,
      defaultNumberOfPeriods: 3,
      periodReductions: {
        // period1: 0,
        // period2: 12,
        // period3: 24,
        // period4: 36,
      },
    },
  },
  dashboard: {
    hasDropdownOnLevel: true,
  },
  createApp: {
    hasEmailExplainer: true,
  },
  createAppSuccess: {
    hasDisclaimer: false,
    hasTrees: false,
  },
  landingPage: {
    sidebarVerticalOffset: '10.5rem',
    hasWelcomePageFooter: true,
    steps: landingPageIntroSteps,
    hasFinePrint: false,
  },
  questionsPage: {
    hasCustomValidationValues: true,
    hasGroupSubtitle: false,
    hasLocationBulkUpload: true,
    location: {
      hasInstructions: false,
    },
    validationValues: {
      otherFuels: 99999999999,
    },
    education: { otherFuelsEducation, electricityEducation },
  },
  summaryPage: {
    hasIcons: true,
  },
  reportingPeriods: {
    month: 12,
    reportingEndDateWithinPastXMonths: null,
  },
  resultsPage: {
    hasTotal: false,
    donut: true,
  },
  paymentPage: {
    hasExtraFieldNote: true,
  },
  settings: {
    allowUserGuideDownloads: true,
    allowOwnerDownloadUserGuide: true,
    hasLongReportingPeriodModal: true,
  },
  defaultVariables: [
    {
      locale: locales.ENGLISH,
      varWordApplication: 'Carbon review',
      varWordBpBusiness: 'bp',
      varWordCustomerType: 'Business',
      varWordApplicationPlural: 'Carbon reviews',
      varWordCustomerTypePlural: 'Businesses',
      varWordApplicationLower: 'carbon review',
      varWordBpBusinessLower: 'bp',
      varWordCustomerTypeLower: 'business',
      varWordApplicationLowerPlural: 'carbon reviews',
      varWordCustomerTypeLowerPlural: 'businesses',
    },
    {
      locale: locales.TEST,
      varWordApplication: 'Carbon review',
      varWordBpBusiness: 'bp',
      varWordCustomerType: 'Business',
      varWordApplicationPlural: 'Carbon reviews',
      varWordCustomerTypePlural: 'Businesses',
      varWordApplicationLower: 'carbon review',
      varWordBpBusinessLower: 'bp',
      varWordCustomerTypeLower: 'business',
      varWordApplicationLowerPlural: 'carbon reviews',
      varWordCustomerTypeLowerPlural: 'businesses',
    },
    {
      locale: locales.CHINESE,
      varWordApplication: 'Carbon review',
      varWordBpBusiness: 'bp',
      varWordCustomerType: 'Business',
      varWordApplicationPlural: 'Carbon reviews',
      varWordCustomerTypePlural: 'Businesses',
      varWordApplicationLower: 'carbon review',
      varWordBpBusinessLower: 'bp',
      varWordCustomerTypeLower: 'business',
      varWordApplicationLowerPlural: 'carbon reviews',
      varWordCustomerTypeLowerPlural: 'businesses',
    },
  ],
  locales: [locales.ENGLISH, locales.TEST],
  images: {
    logoImage: testLogo,
    logoImageMaxHeight: '100px',
    locales: {
      en: {
        exampleReductionDoc: ExampleReductionDocumentEN,
      },
      xx: {
        exampleReductionDoc: ExampleReductionDocumentXX,
      },
      zh: {
        exampleReductionDoc: ExampleReductionDocumentZH,
      },
    },
  },
  components: {
    applicationSettings: {
      DetailsDescription: import('/business/cmpdefault/components/pages/application-settings/DetailsDescription'),
    },
    ReportingPeriodDescription: import(
      '/business/cmpdefault/components/pages/application-settings/ReportingPeriodDescription'
    ),
    TermsAndConditions: import('/business/cmpdefault/components/pages/landing/TermsAndConditions'),

    createApplication: {
      CreateApplicationCountryOfOperationSub: import(
        '/business/cmpdefault/components/pages/create-application/CreateApplicationCountryOfOperationSub'
      ),
    },
  },
  documents: {
    en: { methodologyStatement: null, termsAndConditions: null },
    xx: { methodologyStatement: null, termsAndConditions: null },
    zh: { methodologyStatement: null, termsAndConditions: null },
  },
  reports: {
    // get reports in here
  },

  // TODO: [WR] find a proper solution to hide the contribution widget on create application page
  isDefaultContributionValue: true,
  hasContribution: true,

  // TODO: [PG] we might want to use it for scope-based builds
  hasScopeBasedEmissions: true,
  step1Answers: {
    minimumAnswersRequired: 1,
    allAnswersRequired: false,
  },
  userFields: {
    hasSigningRoles: false,
    hasApproverRole: false,
    hasReviewerRole: false,
  },
};

config.getQuestionsLookup = (() => {
  let cached: QuestionTreeItem[] | null = null;
  return () => {
    if (!cached) {
      cached = getQuestionsLookup();
    }
    return cached;
  };
})();

config.getDecoratedQuestions = (() => {
  let cached: DecoratedQuestionTreeItem[] | null = null;
  return () => {
    if (!cached) {
      cached = decorateQuestions(getConfiguredQuestionsLookup());
    }
    return cached;
  };
})();

config.getFlattenedQuestions = (() => {
  const cache: FlattenedQuestionCache = {};
  return (deep = false) => {
    const key = deep ? 'deep' : 'shallow';
    if (!cache[key]) {
      cache[key] = flattenQuestions(getConfiguredDecoratedQuestions(), deep);
    }
    return cache[key] ?? [];
  };
})();

// config.images.locales

validateConfig(config);

export default config;
